import { useLayoutEffect } from 'react'
import { useWorkspaceSession } from '@/hooks/useWorkspaceSession'
import {
  readScrollPosition,
  saveScrollPosition,
} from '@/store/navigation-scroll.store'
import { useLocation } from 'react-router-dom'

const SCROLLERS =
  '[data-scroll-container], .overflow-auto, .overflow-y-auto, .overflow-scroll, .overflow-y-scroll'

/** Remember both table axes without moving the list when its detail pane opens. */
export function useRouteScrollRestoration() {
  const location = useLocation()
  const scope = useWorkspaceSession()
  const owner = JSON.stringify(scope.key)
  const search = new URLSearchParams(location.search)
  search.delete('task')
  const route = `${location.pathname}?${search.toString()}`

  useLayoutEffect(() => {
    const saved = readScrollPosition(owner, route)
    if (location.hash) return
    let restoring = true
    let frame = 0
    let containers: HTMLElement[] = []
    const findContainers = () => {
      const main = document.getElementById('main-content')
      return main
        ? [...main.querySelectorAll<HTMLElement>(SCROLLERS)].filter(
            element => !element.closest('[role="dialog"], [role="menu"]'),
          )
        : []
    }
    const capture = () => {
      if (restoring) return
      containers = findContainers()
      saveScrollPosition(
        owner,
        route,
        containers.map(element => ({
          top: element.scrollTop,
          left: element.scrollLeft,
        })),
      )
    }
    const observer = new MutationObserver(() => schedule())
    const finish = () => {
      restoring = false
      observer.disconnect()
      window.clearTimeout(timeout)
      cancelAnimationFrame(frame)
    }
    const restore = () => {
      containers = findContainers()
      let ready = containers.length >= (saved?.length ?? 0)
      containers.forEach((element, index) => {
        const position = saved?.[index] ?? { top: 0, left: 0 }
        element.scrollTop = position.top
        element.scrollLeft = position.left
        if (
          element.scrollTop !== position.top ||
          element.scrollLeft !== position.left
        )
          ready = false
      })
      if (ready) finish()
    }
    function schedule() {
      if (!restoring) return
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(restore)
    }
    // Stop retrying when the user takes control; never snap back under a wheel
    // gesture or while the user edits a filter during a slow data restore.
    const takeControl = () => {
      finish()
      capture()
    }
    const timeout = window.setTimeout(() => {
      restore()
      finish()
      capture()
    }, 2000)
    observer.observe(document.body, { childList: true, subtree: true })
    document.addEventListener('scroll', capture, true)
    for (const type of ['wheel', 'touchstart', 'pointerdown', 'keydown'])
      document.addEventListener(type, takeControl, {
        capture: true,
        passive: true,
      })
    schedule()
    return () => {
      // Keep references to the departing DOM, which may already be detached.
      if (!restoring && containers.length)
        saveScrollPosition(
          owner,
          route,
          containers.map(element => ({
            top: element.scrollTop,
            left: element.scrollLeft,
          })),
        )
      finish()
      document.removeEventListener('scroll', capture, true)
      for (const type of ['wheel', 'touchstart', 'pointerdown', 'keydown'])
        document.removeEventListener(type, takeControl, true)
    }
  }, [owner, route, location.hash])
}
